/**
 * ScreenContainer — responsive page wrapper for every screen.
 *
 * Responsibilities:
 *   - Mobile (<768): full-width, safe-area-top padding, ScrollView wrap.
 *   - Tablet / large touch screens: card treatment (boxed surface + shadow)
 *     when the `card` variant is selected. The card fills the viewport
 *     height rather than collapsing to its content, in both the scrolling
 *     and the fixed branch, so a short screen does not bottom out into a
 *     band of bare background.
 *   - Desktop web: no card chrome. Screens either fill the viewport and lay
 *     out internally, or pick a named column width via the variant
 *     (`reading | content | dashboard | app`).
 *
 * The `variant` prop is the canonical way to pick a layout. The older
 * `card` / `maxWidth` / `scroll` props are preserved for back-compat and
 * will be retired once every screen migrates.
 */
import React from 'react'
import { View, type ViewStyle } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useScreenInsets } from '../../lib/hooks/use-screen-insets'
import { RADIUS, SPACING } from '../../lib/theme/tokens'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import {
  COLUMN_PRESETS,
  DESKTOP_SPACING,
  resolveColumnWidth,
  type ColumnPreset,
} from '../../lib/theme/desktop-spacing'
import { useProductTelemetryScroll } from '../../lib/hooks/use-product-telemetry-scroll'
import { LiquidCanvas } from './LiquidBackdrop'
import { GlassSurface } from './GlassSurface'

/**
 * Variant drives both desktop width + chrome. Pick the variant that best
 * describes the screen; only use `maxWidth` for truly one-off cases.
 *
 *   card        — default boxed screen (profile settings, short forms)
 *   full-bleed  — no wrapper card, no ScrollView (maps, wizards, chat)
 *   reading     — narrow 720 column for list/prose (messages, settings)
 *   content     — 800 column for mixed content (profile, trip detail)
 *   dashboard   — 1120 column for grids (hosting, trips)
 *   app         — 1200 column for full app-wide content (explore)
 */
type ScreenVariant = 'card' | 'full-bleed' | 'reading' | 'content' | 'dashboard' | 'app'

const VARIANT_PRESET: Record<Exclude<ScreenVariant, 'card' | 'full-bleed'>, ColumnPreset> = {
  reading: 'reading',
  content: 'content',
  dashboard: 'dashboard',
  app: 'app',
}

interface ScreenContainerProps {
  children: React.ReactNode
  /**
   * The canonical layout variant. Prefer this over `card` / `maxWidth`
   * overrides. When both `variant` and `card`/`maxWidth` are provided,
   * `variant` wins.
   */
  variant?: ScreenVariant
  /** Override the content max-width for the medium-desktop card. Legacy. */
  maxWidth?: number | ColumnPreset
  /** Show the card chrome on medium-desktop. Legacy — use `variant='full-bleed'` instead. */
  card?: boolean
  /** Wrap content in a ScrollView. Default true; false for full-bleed chat, maps, etc. */
  scroll?: boolean
}

export function ScreenContainer({
  children,
  variant,
  maxWidth,
  card = true,
  scroll = true,
}: ScreenContainerProps) {
  // `useScreenInsets`, never `useSafeAreaInsets`: under a visible tab bar the
  // bottom inset is already spent by the bar (it is a flex sibling of the
  // scene, not an overlay), and spending it again here is what left a band of
  // dead background under every tab screen with the last row sliced in half.
  // The hook returns 0 there and the real inset everywhere else — including
  // the ~30 screens that hide the bar and genuinely own the viewport edge.
  const insets = useScreenInsets()
  const { atLeast, bp, isDesktop, isTablet } = useBreakpoint()
  const handleTelemetryScroll = useProductTelemetryScroll('screen_container.scroll')

  // Resolve the effective variant: explicit `variant` prop wins; otherwise
  // fall back to legacy `card` prop (`card={false}` → 'full-bleed').
  const effectiveVariant: ScreenVariant = variant ?? (card === false ? 'full-bleed' : 'card')

  const isTabletCard = isTablet

  // Resolve max-width: variant preset wins, then explicit prop, then default
  // desktop column (`content` = 800) when no variant was given.
  const columnPresetKey =
    effectiveVariant !== 'card' && effectiveVariant !== 'full-bleed'
      ? VARIANT_PRESET[effectiveVariant]
      : null
  const variantMaxWidth = columnPresetKey ? COLUMN_PRESETS[columnPresetKey] : null
  const desktopDefaultMaxWidth =
    isDesktop && effectiveVariant === 'card' ? COLUMN_PRESETS.content : null
  const resolvedMaxWidth =
    variantMaxWidth ??
    (maxWidth != null ? resolveColumnWidth(maxWidth) : (desktopDefaultMaxWidth ?? 800))

  // Content centering: tablet card, desktop variant preset, or desktop default
  // `card` (so sub-screens look cohesive under AppTopNav without per-screen
  // migration). `full-bleed` fills the viewport on every size.
  const shouldCenter =
    (isTabletCard && effectiveVariant !== 'full-bleed') ||
    (isDesktop && columnPresetKey != null) ||
    (isDesktop && effectiveVariant === 'card')

  const centerColumnStyle: ViewStyle | null = shouldCenter
    ? { maxWidth: resolvedMaxWidth, width: '100%', alignSelf: 'center' }
    : null

  // Card chrome: tablet-range only, and only for the `card` variant.
  //
  // `padding` y `marginVertical` suman el desplazamiento que este marco le mete
  // a TODO lo de adentro, incluida la fila del encabezado. Ese total vive en
  // `lib/theme/chrome.ts` como `TABLET_CARD_CHROME`, porque el host de toasts
  // necesita el mismo numero para no pararse encima de la flecha de atras --
  // que es exactamente lo que pasaba cuando cada archivo lo calculaba por su
  // cuenta. Si estos dos valores dejan de ser `SPACING.xl`, hay que mover la
  // constante con ellos; `scripts/validate-toast-clears-navigation.mjs` lo
  // vigila.
  const tabletCardChrome: { padding: number; marginVertical: number } | null =
    isTabletCard && effectiveVariant === 'card'
      ? {
          padding: SPACING.xl,
          marginVertical: SPACING.xl,
        }
      : null

  // Tablet card, scrolling branch only: let the card grow into the viewport.
  //
  // The two branches of this component disagreed. The non-scrolling branch
  // below gives the centred column `flex: 1`, so a `scroll={false}` card
  // fills the screen. The scrolling branch gave the column no flex at all:
  // `contentContainerStyle` carries `flexGrow: 1`, but that only stretches
  // the content *container*, and the column inside it still collapsed to its
  // content height. A card shorter than the viewport therefore bottomed out
  // and handed the remainder back to the bare background — on iPad 13"
  // profile/verification ends at y1652 of 2752 and leaves 1100px (40% of the
  // canvas) of grey under a 55px top inset. That asymmetry is what reads as
  // unfinished. This makes the scrolling card behave like the fixed one.
  //
  // `flexGrow` and not `flex`: `flex: 1` also sets `flexBasis: 0` and
  // `flexShrink: 1`, which inside a ScrollView content container can squash
  // content taller than the viewport. `flexGrow` only ever grows, so tall
  // screens keep their natural height and scroll exactly as before.
  const tabletCardFillStyle: ViewStyle | null =
    isTabletCard && effectiveVariant === 'card' ? { flexGrow: 1 } : null

  const mobileScrollStyle: ViewStyle = {
    flex: 1,
    paddingTop: atLeast('md') ? 0 : insets.top,
  }

  const mobileFixedStyle: ViewStyle = {
    ...mobileScrollStyle,
    paddingBottom: atLeast('md') ? 0 : insets.bottom,
  }

  const contentStyle: ViewStyle = centerColumnStyle ?? mobileScrollStyle
  const usesTabletGlassCard = tabletCardChrome !== null

  const tabletGlassStyle: ViewStyle | null = usesTabletGlassCard
    ? {
        ...centerColumnStyle,
        borderRadius: RADIUS.xl,
        marginVertical: tabletCardChrome.marginVertical,
      }
    : null

  const tabletGlassContentStyle: ViewStyle | null = usesTabletGlassCard
    ? {
        flexGrow: 1,
        padding: tabletCardChrome.padding,
      }
    : null

  // Desktop rhythm for boxed screens: under AppTopNav, `card` / column
  // variants share shellPaddingX/Y so they align with the primary tabs.
  // full-bleed owns the viewport edge. Padding here stacked with AppTopNav
  // and forced every editorial canvas (checkout, tours, split) to
  // negative-margin it back. Split forgot and showed a pale band plus a
  // double gutter. Skip desktop shell pad when the variant is full-bleed.
  const desktopShellPaddingX =
    isDesktop && effectiveVariant !== 'full-bleed' ? DESKTOP_SPACING.shellPaddingX[bp] : 0
  const desktopShellPaddingY =
    isDesktop && effectiveVariant !== 'full-bleed' ? DESKTOP_SPACING.shellPaddingY[bp] : 0

  // full-bleed + scroll=true still wraps in a ScrollView for consistency;
  // callers that manage their own ScrollView pass scroll={false}.
  const effectiveScroll = effectiveVariant === 'full-bleed' ? scroll : scroll

  if (!effectiveScroll) {
    return (
      <LiquidCanvas>
        <View
          style={{
            flex: 1,
            paddingTop: isDesktop ? desktopShellPaddingY : isTablet ? insets.top : 0,
            paddingHorizontal: isDesktop ? desktopShellPaddingX : 0,
          }}
        >
          {usesTabletGlassCard ? (
            <GlassSurface
              tone="regular"
              radius={RADIUS.xl}
              style={{ ...tabletGlassStyle, flex: 1 }}
              contentStyle={tabletGlassContentStyle}
            >
              {children}
            </GlassSurface>
          ) : (
            <View style={centerColumnStyle ? { ...contentStyle, flex: 1 } : mobileFixedStyle}>
              {children}
            </View>
          )}
        </View>
      </LiquidCanvas>
    )
  }

  return (
    <LiquidCanvas>
      <ScrollView
        style={{ flex: 1, backgroundColor: 'transparent' }}
        onScroll={handleTelemetryScroll}
        scrollEventThrottle={250}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: insets.bottom + SPACING.lg,
          paddingTop: isDesktop ? desktopShellPaddingY : isTablet ? insets.top : 0,
          ...(isTabletCard ? { paddingHorizontal: SPACING.lg } : {}),
          ...(isDesktop ? { paddingHorizontal: desktopShellPaddingX } : {}),
        }}
        showsVerticalScrollIndicator={false}
      >
        {usesTabletGlassCard ? (
          <GlassSurface
            tone="regular"
            radius={RADIUS.xl}
            style={{ ...tabletGlassStyle, ...tabletCardFillStyle }}
            contentStyle={tabletGlassContentStyle}
          >
            {children}
          </GlassSurface>
        ) : (
          <View
            style={
              centerColumnStyle ? { ...contentStyle, ...tabletCardFillStyle } : mobileScrollStyle
            }
          >
            {children}
          </View>
        )}
      </ScrollView>
    </LiquidCanvas>
  )
}
